import { useMemo, useRef, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Input } from '@/components/ui/input';

// Custom typeahead replacing native <datalist>, which renders an unstylable
// OS dropdown polluted with the browser's own saved form history. Suggestions
// render in an app-themed popover: prefix matches ranked first, matched text
// highlighted, full keyboard navigation (arrows / Enter / Escape). Free text
// is always allowed; selecting a suggestion just fills the input.
export function AutocompleteInput({
  id, value, onChange, options, placeholder, maxVisible = 8,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  maxVisible?: number;
}) {
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  const query = value.trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (!query) return [];
    const prefix: string[] = [];
    const substring: string[] = [];
    for (const o of options) {
      const lower = o.toLowerCase();
      if (lower === query) continue;
      if (lower.startsWith(query)) prefix.push(o);
      else if (lower.includes(query)) substring.push(o);
    }
    return [...prefix, ...substring].slice(0, maxVisible);
  }, [options, query, maxVisible]);

  const open = focused && !dismissed && suggestions.length > 0;

  function select(option: string) {
    onChange(option);
    setDismissed(true);
    setActiveIndex(-1);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      if (e.key === 'ArrowDown' && suggestions.length > 0) setDismissed(false);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      const next = (activeIndex + delta + suggestions.length + 1) % (suggestions.length + 1);
      const idx = next === suggestions.length ? -1 : next;
      setActiveIndex(idx);
      if (idx >= 0) listRef.current?.children[idx]?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter') {
      const active = suggestions[activeIndex];
      if (active !== undefined) {
        e.preventDefault();
        select(active);
      }
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setDismissed(true);
      setActiveIndex(-1);
    } else if (e.key === 'Tab') {
      setDismissed(true);
    }
  }

  return (
    <PopoverPrimitive.Root open={open}>
      <PopoverPrimitive.Anchor asChild>
        <Input
          id={id}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          onChange={(e) => {
            onChange(e.target.value);
            setDismissed(false);
            setActiveIndex(-1);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setDismissed(false);
            setActiveIndex(-1);
          }}
          onKeyDown={onKeyDown}
        />
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="z-50 w-[var(--radix-popper-anchor-width)] rounded-[var(--radius-card)] border border-border bg-surface shadow-md"
        >
          <div ref={listRef} role="listbox" className="max-h-64 overflow-y-auto p-1">
            {suggestions.map((s, i) => (
              <button
                key={s}
                type="button"
                role="option"
                aria-selected={i === activeIndex}
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(s)}
                onMouseEnter={() => setActiveIndex(i)}
                className={`flex w-full items-center rounded px-2.5 py-2 text-left text-sm text-ink ${i === activeIndex ? 'bg-surface-2' : ''}`}
              >
                <HighlightMatch text={s} query={query} />
              </button>
            ))}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

// Bolds the typed portion inside each suggestion (like Google / Stripe
// autocompletes) so the eye can scan what completes the query.
function HighlightMatch({ text, query }: { text: string; query: string }) {
  const idx = query ? text.toLowerCase().indexOf(query) : -1;
  if (idx < 0) return <span className="truncate">{text}</span>;
  return (
    <span className="truncate">
      {text.slice(0, idx)}
      <span className="font-semibold text-accent">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </span>
  );
}
